import { useMemo, useState } from 'react';

import type { HpChildComponentProps } from 'components/shared/componentWithModal/componentWithModal';
import { PageNavigation } from 'components/layout/navigation/pageNavigation';
import { Title } from 'components/layout/header/title';
import { getYearString } from 'utils';
import { PATH } from 'interfaces/pathTypes';
import { DOCUMENT_TYPE_CODE, PolicyDocument } from 'interfaces/customerDataTypes';
import { DocumentsGroup } from './documentsGroup';

import './documents.scss';
import { HpAlert } from 'components/shared/alert';
import { HpSvgIconTypes } from '@avantia/style-guide';

type TabOptions = 'current' | 'past';

interface GroupedDocuments {
  currentDoc: PolicyDocument | null;
  sortedPastDocs: PolicyDocument[] | null;
  renewalDoc: PolicyDocument | null;
}

const getGroupedDocuments = (documents: PolicyDocument[]): GroupedDocuments => {
  if (documents.length === 0) {
    return {
      currentDoc: null,
      sortedPastDocs: null,
      renewalDoc: null
    };
  }

  const documentsSortedByDate = [...documents].sort(
    (a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
  );

  const renewalDocIndex = documentsSortedByDate.findIndex(
    (doc) => doc.documentType === DOCUMENT_TYPE_CODE.RENEWAL_INVITE_DOC
  );
  const renewalDoc = renewalDocIndex > -1 && documentsSortedByDate.splice(renewalDocIndex, 1)[0];

  return {
    currentDoc: documentsSortedByDate[0],
    sortedPastDocs: documentsSortedByDate.length > 1 ? documentsSortedByDate.slice(1) : null,
    renewalDoc: renewalDoc || null
  };
};

export const Documents = ({ selectedPolicy }: HpChildComponentProps): JSX.Element => {
  const [tab, setTab] = useState<TabOptions>('current');
  const { documents } = selectedPolicy;

  const { currentDoc, sortedPastDocs, renewalDoc } = useMemo(() => getGroupedDocuments(documents), [documents]);

  const handleClick = (selectedTab: TabOptions) => setTab(selectedTab);

  return (
    <>
      <Title title="POLICY DOCUMENTS" icon="document-circle" id="documents" />
      <div className="documents-container">
        {documents.length === 0 ? (
          <HpAlert
            className="no-documents-found-alert"
            alertLevel="error"
            iconType={'exclamation' as HpSvgIconTypes}
            message={<p className="alert-text">Policy documents not found - please try again later.</p>}
          />
        ) : (
          <>
            <h2>YOUR HOME INSURANCE DOCUMENTS</h2>
            {sortedPastDocs && sortedPastDocs.length > 0 && (
              <div className={tab === 'current' ? 'documents-tab-current' : 'documents-tab-past'}>
                <button onClick={() => handleClick('current')} className="current">
                  <p>Current</p>
                </button>
                <button onClick={() => handleClick('past')} className="past">
                  <p>Past</p>
                </button>
              </div>
            )}
            {tab === 'current' && (
              <>
                {renewalDoc && (
                  <DocumentsGroup name="Renewal" selectedPolicy={selectedPolicy} policyDocument={renewalDoc} />
                )}
                {currentDoc && (
                  <DocumentsGroup name="Current" selectedPolicy={selectedPolicy} policyDocument={currentDoc} />
                )}
              </>
            )}
            {tab === 'past' && sortedPastDocs && (
              <>
                {sortedPastDocs.map((pastDoc) => (
                  <div key={pastDoc.documentArchiveId}>
                    <DocumentsGroup
                      name={String(getYearString(pastDoc.effectiveDate))}
                      selectedPolicy={selectedPolicy}
                      policyDocument={pastDoc}
                    />
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
      <PageNavigation page={PATH.DOCUMENTS} />
    </>
  );
};
